import React, { useState, useEffect, useCallback } from 'react';

export default function GalleryLightbox({ images = [], isOpen, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(true);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setIsZoomed(false);
  }, [initialIndex, isOpen]);

  const handleNext = useCallback(() => {
    if (!images || images.length === 0) return;
    setIsZoomed(false);
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images]);

  const handlePrev = useCallback(() => {
    if (!images || images.length === 0) return;
    setIsZoomed(false);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images]);

  const toggleZoom = useCallback(() => {
    setIsZoomed((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        if (isZoomed) {
          setIsZoomed(false);
        } else {
          onClose();
        }
      }
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === '+' || e.key === '=') setIsZoomed(true);
      if (e.key === '-') setIsZoomed(false);
    },
    [isOpen, onClose, handleNext, handlePrev, isZoomed]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
      setIsZoomed(false);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const rawUrl = typeof currentImage === 'string' ? currentImage : currentImage?.url || currentImage?.src || '';
  const caption = typeof currentImage === 'object' ? currentImage?.caption || currentImage?.alt || currentImage?.title : '';

  // Cloudflare CDN optimized high-res URL for crisp full-screen presentation
  const highResUrl = rawUrl.startsWith('https://assets.auxodev.dk/') && !rawUrl.includes('/cdn-cgi/image/')
    ? `https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=90,format=auto/${rawUrl.replace('https://assets.auxodev.dk/', '')}`
    : rawUrl;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-md select-none animate-fadeIn"
      role="dialog"
      aria-label="Billedvisning i fuld skærm"
      aria-modal="true"
    >
      {/* Top Header Controls Bar */}
      <div className="w-full flex items-center justify-between px-4 sm:px-6 py-3 bg-gradient-to-b from-black/80 to-transparent z-20 shrink-0">
        <div className="flex items-center gap-3">
          <span className="bg-zinc-900/90 text-[#F4AF00] font-black text-xs sm:text-sm px-3 py-1 rounded border border-zinc-700 tracking-wider">
            {currentIndex + 1} / {images.length}
          </span>
          {caption && (
            <span className="text-zinc-300 text-xs sm:text-sm font-medium hidden md:inline-block max-w-md lg:max-w-xl truncate">
              {caption}
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* Zoom toggle */}
          <button
            onClick={toggleZoom}
            className="p-2.5 rounded-lg bg-zinc-900/90 hover:bg-[#F4AF00] hover:text-[#121212] text-zinc-200 transition-colors border border-zinc-800 text-sm flex items-center gap-1.5 cursor-pointer"
            aria-label={isZoomed ? "Zoom ud" : "Zoom ind"}
            title={isZoomed ? "Zoom ud (-)" : "Zoom ind (+)"}
          >
            <i className={`fa-solid ${isZoomed ? 'fa-magnifying-glass-minus' : 'fa-magnifying-glass-plus'}`} aria-hidden="true" />
            <span className="hidden sm:inline text-xs font-bold">{isZoomed ? 'Formindsk' : 'Forstør'}</span>
          </button>

          {/* Toggle thumbnails */}
          {images.length > 1 && (
            <button
              onClick={() => setShowThumbnails((prev) => !prev)}
              className={`p-2.5 rounded-lg transition-colors border text-sm flex items-center gap-1.5 cursor-pointer ${
                showThumbnails
                  ? 'bg-zinc-900/90 hover:bg-zinc-800 text-zinc-200 border-zinc-800'
                  : 'bg-zinc-800 text-[#F4AF00] border-zinc-700'
              }`}
              aria-label={showThumbnails ? "Skjul miniaturebilleder" : "Vis miniaturebilleder"}
              title="Miniaturer"
            >
              <i className="fa-solid fa-table-cells text-xs" aria-hidden="true" />
            </button>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-2.5 rounded-lg bg-zinc-900/90 hover:bg-red-600 text-zinc-200 hover:text-white transition-colors border border-zinc-800 cursor-pointer"
            aria-label="Luk billedvisning"
            title="Luk (Esc)"
          >
            <i className="fa-solid fa-xmark text-base sm:text-lg" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Main Image Stage - Maximized viewport */}
      <div className="relative flex-1 w-full flex items-center justify-center p-2 sm:p-4 overflow-hidden">
        {/* Navigation Arrow Left */}
        {images.length > 1 && (
          <button
            onClick={handlePrev}
            className="absolute left-2 sm:left-6 z-30 p-3 sm:p-4 rounded-full bg-zinc-950/80 hover:bg-[#F4AF00] hover:text-[#121212] text-white transition-all shadow-2xl border border-zinc-800/80 focus:outline-none hover:scale-110 active:scale-95 cursor-pointer"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-lg sm:text-xl" aria-hidden="true" />
          </button>
        )}

        {/* The Big Image Container */}
        <div
          onClick={toggleZoom}
          className={`relative max-w-full max-h-full flex items-center justify-center transition-transform duration-200 cursor-pointer ${
            isZoomed ? 'overflow-auto cursor-zoom-out scale-125 md:scale-150' : 'cursor-zoom-in'
          }`}
          title={isZoomed ? "Klik for at formindske" : "Klik for at forstørre"}
        >
          <img
            key={highResUrl}
            src={highResUrl}
            alt={caption || `Galleribillede ${currentIndex + 1}`}
            className="max-h-[82vh] sm:max-h-[86vh] w-auto max-w-[94vw] lg:max-w-[92vw] object-contain rounded-md shadow-2xl transition-all duration-200"
            loading="eager"
          />
        </div>

        {/* Navigation Arrow Right */}
        {images.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-2 sm:right-6 z-30 p-3 sm:p-4 rounded-full bg-zinc-950/80 hover:bg-[#F4AF00] hover:text-[#121212] text-white transition-all shadow-2xl border border-zinc-800/80 focus:outline-none hover:scale-110 active:scale-95 cursor-pointer"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-lg sm:text-xl" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Caption overlay at bottom (when available) */}
      {caption && (
        <div className="w-full text-center px-4 py-2 z-10">
          <p className="inline-block text-zinc-200 text-xs sm:text-sm font-semibold bg-zinc-950/85 px-4 py-1.5 rounded-full border border-zinc-800 shadow-md">
            {caption}
          </p>
        </div>
      )}

      {/* Thumbnail Bar at Bottom */}
      {images.length > 1 && showThumbnails && (
        <div className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-t from-black/90 via-black/60 to-transparent z-20 shrink-0">
          <div className="flex items-center gap-2 overflow-x-auto max-w-5xl py-1 px-2 no-scrollbar">
            {images.map((img, idx) => {
              const rawThumb = typeof img === 'string' ? img : img?.url || img?.src || '';
              const thumbUrl = rawThumb.startsWith('https://assets.auxodev.dk/') && !rawThumb.includes('/cdn-cgi/image/')
                ? `https://assets.auxodev.dk/cdn-cgi/image/width=160,quality=75,format=auto/${rawThumb.replace('https://assets.auxodev.dk/', '')}`
                : rawThumb;

              return (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsZoomed(false);
                    setCurrentIndex(idx);
                  }}
                  className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden border-2 transition-all flex-shrink-0 focus:outline-none cursor-pointer ${
                    idx === currentIndex
                      ? 'border-[#F4AF00] ring-2 ring-[#F4AF00]/40 scale-105 opacity-100 z-10'
                      : 'border-zinc-800 opacity-50 hover:opacity-100'
                  }`}
                  aria-label={`Gå til billede ${idx + 1}`}
                >
                  <img src={thumbUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
